/**
 * Decision Room
 * A decision framed properly: what it depends on, what the data says, what is
 * missing and who owes it, the options side by side, and a readiness score.
 * Demo content - see src/demo/data.ts.
 */

import React from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, PageHead, Split, Note, Table, Tag, ProgRow, DemoBadge } from '../../components/ui';
import { DECISION } from '../../demo/data';

const stateStyles: Record<string, { bg: string; fg: string; glyph: string }> = {
  done: { bg: 'var(--green-s)', fg: '#0A7A55', glyph: '✓' },
  part: { bg: 'var(--amber-s)', fg: '#8A5C05', glyph: '◐' },
  miss: { bg: 'var(--red-s)', fg: '#963120', glyph: '✗' },
};

export const DecisionRoomPage: React.FC = () => {
  const d = DECISION;
  const done = d.inputs.filter((i) => i.state === 'done').length;
  const part = d.inputs.filter((i) => i.state === 'part').length;
  const miss = d.inputs.filter((i) => i.state === 'miss').length;
  const total = d.inputs.length;

  return (
    <AppShell
      title="Decision Room"
      sub="Riyadh regional office"
      actions={
        <>
          <DemoBadge />
          <button className="btn btn-g">All decisions</button>
          <button className="btn btn-p">New decision</button>
        </>
      }
    >
      <PageHead
        eyebrow="Decision Room"
        title="Riyadh regional office"
        lede="A decision framed properly: what it depends on, what the data says, what is still missing and who owes it, the options side by side, and a readiness score that tells you whether you are deciding on evidence or instinct."
      />

      <div className="dq" style={{ marginBottom: 12 }}>
        <div className="e">{d.eyebrow}</div>
        <h3>{d.question}</h3>
        <div className="row3">
          {d.facts.map((f) => (
            <div key={f.label}>
              <span>{f.label}</span>
              <b style={f.label === 'Readiness' ? { color: '#F5C26B' } : undefined}>{f.value}</b>
            </div>
          ))}
        </div>
      </div>

      <div className="g g3" style={{ marginBottom: 12 }}>
        <div className="s2">
          <Card
            title="Data required — and who owes it"
            sub={`${total} inputs · ${done} automatic · ${part} modelled · ${miss} owed`}
            right={<button className="btn btn-g btn-s">Chase owners</button>}
          >
            {d.inputs.map((input) => {
              const st = stateStyles[input.state];
              return (
                <div className="dreq" key={input.name}>
                  <span className="st2" style={{ background: st.bg, color: st.fg }}>
                    {st.glyph}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{input.name}</div>
                    <div className="t2">{input.detail}</div>
                  </div>
                  <Tag>{input.source}</Tag>
                  <Tag
                    tone={
                      input.owner === 'Automatic' ? 'g' : input.owner.startsWith('Due') ? 'a' : 'b'
                    }
                  >
                    {input.owner}
                  </Tag>
                </div>
              );
            })}
          </Card>
        </div>

        <div>
          <Card title="Readiness">
            <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
              <div
                className="mono"
                style={{ fontSize: 44, letterSpacing: '-.04em', color: 'var(--amber)' }}
              >
                {d.readiness}%
              </div>
              <div className="dim" style={{ fontSize: 12, fontWeight: 600 }}>
                {d.readinessNote}
              </div>
            </div>
            <ProgRow label="Evidence available" value={`${done}/${total}`} pct={(done / total) * 100} color="var(--green)" />
            <ProgRow label="Modelled" value={`${part}/${total}`} pct={(part / total) * 100} color="var(--amber)" />
            <ProgRow label="Owed by people" value={`${miss}/${total}`} pct={(miss / total) * 100} color="var(--red)" />
            <Note style={{ marginTop: 11 }}>
              OmniMind will recommend deciding once readiness passes <b>80%</b>. The three owed inputs
              would take it to <b>82%</b>.
            </Note>
          </Card>

          <div style={{ height: 12 }} />

          <div className="ink-card">
            <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 9 }}>
              <span className="av2" style={{ width: 26, height: 26, borderRadius: 9 }}>
                <i style={{ width: 9, height: 9 }} />
              </span>
              <b style={{ fontSize: 13 }}>What I would watch</b>
            </div>
            <p>{d.watch}</p>
          </div>
        </div>
      </div>

      <div className="g g3" style={{ marginBottom: 12 }}>
        {d.options.map((o) => (
          <div className={`opt${'recommended' in o && o.recommended ? ' rec' : ''}`} key={o.key}>
            <div className="lab">
              Option {o.key}
              {'recommended' in o && o.recommended && (
                <span style={{ color: 'var(--green)' }}> · recommended</span>
              )}
            </div>
            <h4>{o.title}</h4>
            <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginTop: 5 }}>
              {o.blurb}
            </p>
            <div style={{ marginTop: 11 }}>
              <Split label="Year-1 cost" value={o.cost} />
              <Split label="Payback" value={o.payback} />
              <Split label="Risk" value={o.risk} />
            </div>
            <Note style={{ marginTop: 9 }}>{o.note}</Note>
          </div>
        ))}
      </div>

      <Card
        title="Scenario — what has to be true for Option B to pay back in year two"
        sub="one assumption has no data behind it"
        flush
      >
        <Table
          cols={[
            { t: 'Assumption' },
            { t: 'Base case', num: true },
            { t: 'Needed', num: true },
            { t: 'Source' },
            { t: 'Confidence' },
          ]}
          rows={d.scenario.map((r) => [
            r.assumption,
            r.base,
            r.needed,
            r.source,
            <Tag tone={r.tone as any}>{r.confidence}</Tag>,
          ])}
        />
      </Card>
    </AppShell>
  );
};
